import { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import { enableAdminPush, isAdminPushActive } from "@/lib/adminPush";

const DISMISS_KEY = "gc_admin_push_nudge_dismissed_until";
const DISMISS_DAYS = 7;

/**
 * One-tap "Enable push notifications" banner on the Live Chat page.
 * Shows when the admin's browser supports Web Push, push isn't already
 * active, and the nudge hasn't been snoozed in the last 7 days.
 */
export default function AdminPushNudge({ onEnabled }) {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const supported =
      "Notification" in window &&
      "serviceWorker" in navigator &&
      "PushManager" in window;
    if (!supported) {
      setVisible(false);
      return;
    }
    if (Notification.permission === "denied") {
      // Browser blocked permanently — the nudge would be useless.
      setVisible(false);
      return;
    }
    const dismissedUntil = parseInt(
      localStorage.getItem(DISMISS_KEY) || "0",
      10,
    );
    if (dismissedUntil > Date.now()) {
      setVisible(false);
      return;
    }
    isAdminPushActive()
      .then((active) => setVisible(!active))
      .catch(() => setVisible(true));
  }, []);

  const dismiss = () => {
    const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(DISMISS_KEY, String(until));
    setVisible(false);
  };

  const onEnable = async () => {
    setBusy(true);
    try {
      await enableAdminPush();
      toast.success("Push alerts enabled — your phone will ping for new chats 🔔");
      setVisible(false);
      onEnabled?.();
    } catch (e) {
      toast.error(e?.message || "Could not enable notifications");
    } finally {
      setBusy(false);
    }
  };

  if (!visible) return null;

  return (
    <div
      data-testid="admin-push-nudge"
      className="mb-3 rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 px-4 py-3 flex items-center gap-3"
    >
      <div className="w-9 h-9 rounded-lg bg-emerald-600 text-white inline-flex items-center justify-center flex-shrink-0">
        <Bell className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0 leading-tight">
        <div className="text-sm font-semibold text-slate-900">
          Never miss a customer chat
        </div>
        <div className="text-xs text-slate-600">
          Turn on phone push so the very next message rings your device — even when this tab is closed.
        </div>
      </div>
      <button
        type="button"
        onClick={onEnable}
        disabled={busy}
        data-testid="admin-push-nudge-cta"
        className="inline-flex items-center gap-1 h-9 px-3 rounded-lg text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 flex-shrink-0"
      >
        <Bell className="w-3.5 h-3.5" />
        {busy ? "Enabling…" : "Turn on"}
      </button>
      <button
        type="button"
        onClick={dismiss}
        data-testid="admin-push-nudge-dismiss"
        className="h-9 w-9 rounded-lg hover:bg-emerald-100 inline-flex items-center justify-center text-slate-500 flex-shrink-0"
        aria-label="Dismiss for 7 days"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
